import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import GoalCard from '../common/GoalCard';

const GoalList = () => {
  const [goals, setGoals] = useState([]);
  const [operations, setOperations] = useState([]);
  const [units, setUnits] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    operation: '',
    unit: '',
    department: '',
    period: '',
    period_type: '',
    search: '', // Busca por título e descrição
    date_filter: '' // Formato: YYYY-MM
  });

  // Verificar se há filtro de data ativo
  const hasDateFilter = filters.date_filter !== '';

  const loadData = useCallback(async () => {
    try {
      setLoading(true);
      
      // Carregar metas
      const goalsResponse = await axios.get('/goals', { params: filters });
      if (goalsResponse.data.success) {
        setGoals(goalsResponse.data.goals || []);
      }

      // Carregar operações, unidades e departamentos para filtros
      const dashboardResponse = await axios.get('/dashboard');
      if (dashboardResponse.data.success) {
        setOperations(dashboardResponse.data.data.operations || []);
        setUnits(dashboardResponse.data.data.units || []);
        setDepartments(dashboardResponse.data.data.departments || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Callback para atualizar uma meta na lista após atualização de progresso
  const handleGoalUpdate = (updatedGoal) => {
    setGoals(prevGoals => 
      prevGoals.map(goal => 
        goal.id === updatedGoal.id ? { ...goal, ...updatedGoal } : goal
      )
    );
  };

  // Filtrar metas por status
  // Se há filtro de data, mostrar todas as metas (incluindo concluídas)
  // Se não há filtro de data, mostrar apenas metas ativas
  const filteredGoals = hasDateFilter 
    ? goals 
    : goals.filter(goal => goal.status === 'active');

  // Deduplicar por família (parent_goal_id):
  // Mostrar a meta ativa mais antiga POSTERIOR à última meta concluída da família
  const deduplicatedGoals = (() => {
    if (hasDateFilter) return filteredGoals; // Com filtro de data, mostrar todos

    // 1. Encontrar a última meta concluída de cada família (maior start_date com status 'concluded')
    const lastConcluded = {};
    // Também rastrear a meta mais nova (active ou concluded) de cada família para navegação
    const latestGoalPerFamily = {};
    goals.forEach(goal => {
      const familyKey = String(goal.parent_goal_id || goal.id);
      const goalDate = new Date(goal.start_date || 0);

      if (goal.status === 'concluded') {
        if (!lastConcluded[familyKey] || goalDate > new Date(lastConcluded[familyKey].start_date || 0)) {
          lastConcluded[familyKey] = goal;
        }
      }

      // Rastrear a meta mais recente (active ou concluded) para navegação
      if (goal.status === 'active' || goal.status === 'concluded') {
        if (!latestGoalPerFamily[familyKey] || goalDate > new Date(latestGoalPerFamily[familyKey].start_date || 0)) {
          latestGoalPerFamily[familyKey] = goal;
        }
      }
    });

    // 2. Para cada família, escolher a meta ativa mais antiga que seja posterior à última concluída
    const families = {};       // metas posteriores à última concluída
    const fallbacks = {};      // fallback: meta ativa mais recente (caso nenhuma passe o filtro)
    
    filteredGoals.forEach(goal => {
      const familyKey = String(goal.parent_goal_id || goal.id);
      const goalDate = new Date(goal.start_date || 0);

      // Sempre rastrear a meta ativa mais recente como fallback
      if (!fallbacks[familyKey] || goalDate > new Date(fallbacks[familyKey].start_date || 0)) {
        fallbacks[familyKey] = goal;
      }

      // Se há uma meta concluída nesta família, ignorar ativas anteriores a ela
      const lastConcludedGoal = lastConcluded[familyKey];
      if (lastConcludedGoal) {
        const concludedDate = new Date(lastConcludedGoal.start_date || 0);
        if (goalDate < concludedDate) {
          return; // Pular: esta meta ativa é de um período anterior à última concluída
        }
      }

      if (!families[familyKey]) {
        families[familyKey] = goal;
      } else {
        // Manter o mais antigo (menor start_date) dentre os posteriores à última concluída
        const existing = families[familyKey];
        const existingDate = new Date(existing.start_date || 0);
        if (goalDate < existingDate) {
          families[familyKey] = goal;
        }
      }
    });

    // 3. Para famílias sem meta posterior à última concluída, usar o fallback (meta ativa mais recente)
    Object.keys(fallbacks).forEach(familyKey => {
      if (!families[familyKey]) {
        families[familyKey] = fallbacks[familyKey];
      }
    });

    // 4. Atribuir latest_goal_id a cada meta para navegação
    return Object.values(families).map(goal => {
      const familyKey = String(goal.parent_goal_id || goal.id);
      const latestGoal = latestGoalPerFamily[familyKey];
      return {
        ...goal,
        latest_goal_id: latestGoal ? latestGoal.id : goal.id
      };
    });
  })();

  // Ordenar metas: 1º alfabético por título, 2º por data de atualização (mais recente primeiro)
  const sortedGoals = [...deduplicatedGoals].sort((a, b) => {
    // Primeiro: ordem alfabética por título
    const titleCompare = (a.title || '').localeCompare(b.title || '', 'pt-BR');
    if (titleCompare !== 0) return titleCompare;
    
    // Segundo: data de atualização (mais recente primeiro)
    const dateA = new Date(a.updated_at || a.created_at || 0);
    const dateB = new Date(b.updated_at || b.created_at || 0);
    return dateB - dateA;
  });

  // Agrupar metas por operação e unidade (similar ao template Django)
  const groupedGoals = {};
  sortedGoals.forEach(goal => {
    const opKey = goal.operation_name || 'Sem Operação';
    const unitKey = goal.unit_name || 'Sem Unidade';
    
    if (!groupedGoals[opKey]) {
      groupedGoals[opKey] = {};
    }
    if (!groupedGoals[opKey][unitKey]) {
      groupedGoals[opKey][unitKey] = [];
    }
    groupedGoals[opKey][unitKey].push(goal);
  });

  return (
    <div className="goals-list-page">
      {/* Header + Filtros Unificados */}
      <div className="goals-toolbar">
        <div className="goals-toolbar-row">
          {/* Título compacto */}
          <div className="goals-toolbar-title">
            <h1><i className="fas fa-bullseye"></i> Metas</h1>
            {hasDateFilter && (
              <span className="goals-date-badge">
                {(() => {
                  const [y, m] = filters.date_filter.split('-').map(Number);
                  const months = ['jan.', 'fev.', 'mar.', 'abr.', 'mai.', 'jun.', 'jul.', 'ago.', 'set.', 'out.', 'nov.', 'dez.'];
                  return `${months[m - 1]} de ${y}`;
                })()}
              </span>
            )}
          </div>

          {/* Ações */}
          <div className="goals-toolbar-actions">
            <Link to="/goals/create" className="goals-toolbar-btn primary" title="Nova Meta">
              <i className="fas fa-plus"></i>
            </Link>
            <Link to="/dashboard" className="goals-toolbar-btn success" title="Dashboard">
              <i className="fas fa-chart-line"></i>
            </Link>
            <Link to="/goals/inactive" className="goals-toolbar-btn warning" title="Inativas">
              <i className="fas fa-pause-circle"></i>
            </Link>
          </div>
        </div>

        {/* Filtros em linha */}
        <div className="goals-toolbar-filters">
          <select 
            className="goals-filter-select"
            value={filters.operation}
            onChange={(e) => handleFilterChange('operation', e.target.value)}
            title="Operação"
          >
            <option value="">Operação</option>
            {operations.map(op => (
              <option key={op.id} value={op.code}>{op.name}</option>
            ))}
          </select>

          <select 
            className="goals-filter-select"
            value={filters.unit}
            onChange={(e) => handleFilterChange('unit', e.target.value)}
            title="Unidade"
          >
            <option value="">Unidade</option>
            {units.map(unit => (
              <option key={unit.id} value={unit.code}>{unit.name}</option>
            ))}
          </select>

          <select 
            className="goals-filter-select"
            value={filters.department}
            onChange={(e) => handleFilterChange('department', e.target.value)}
            title="Departamento"
          >
            <option value="">Depto</option>
            {departments.map(dept => (
              <option key={dept} value={dept}>{dept}</option>
            ))}
          </select>

          <div className="goals-filter-search">
            <i className="fas fa-search"></i>
            <input
              type="text"
              placeholder="Buscar título ou descrição..."
              value={filters.search}
              onChange={(e) => handleFilterChange('search', e.target.value)}
            />
          </div>

          <div className="goals-filter-date">
            <input
              type="month"
              value={filters.date_filter}
              onChange={(e) => handleFilterChange('date_filter', e.target.value)}
              title="Filtrar por período"
            />
            {filters.date_filter && (
              <button 
                type="button" 
                className="goals-filter-clear"
                onClick={() => handleFilterChange('date_filter', '')}
                title="Limpar"
              >
                <i className="fas fa-times"></i>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Lista de Metas Agrupadas */}
      {loading ? (
        <div className="loading">Carregando...</div>
      ) : sortedGoals.length === 0 ? (
        <div className="no-goals">
          <p>{hasDateFilter ? 'Nenhuma meta encontrada para este período' : 'Nenhuma meta ativa encontrada'}</p>
          {!hasDateFilter && goals.length > 0 && (
            <p className="text-muted small mt-2">
              <i className="fas fa-info-circle me-1"></i>
              Use o filtro de data para ver metas concluídas
            </p>
          )}
        </div>
      ) : (
        <div className="operations-grid">
          {Object.keys(groupedGoals).map(operationName => {
            const operationGoals = groupedGoals[operationName];
            const allGoalsInOp = Object.values(operationGoals).flat();
            const totalGoals = allGoalsInOp.length;
            const completedGoals = allGoalsInOp.filter(g => g.status === 'concluded').length;
            const activeGoals = allGoalsInOp.filter(g => g.status === 'active').length;
            const avgProgress = allGoalsInOp.length > 0
              ? allGoalsInOp.reduce((sum, g) => sum + parseFloat(g.progress_percentage || 0), 0) / allGoalsInOp.length
              : 0;

            return (
              <section key={operationName} className="period-section operation-section">
                {/* Operation Header */}
                <div className="period-header period-header-bg-goals">
                  <h4 className="period-title">
                    <i className="fas fa-building"></i>
                    {operationName}
                  </h4>
                  <span className="period-count">
                    {Object.keys(operationGoals).length} Unidades
                  </span>
                </div>

                {/* Operation Stats */}
                <div className="goals-stats-overview">
                  <div className="goals-stat-card">
                    <div className="goals-stat-icon"><i className="fas fa-bullseye"></i></div>
                    <div className="goals-stat-content">
                      <span className="goals-stat-label">Total Ativas</span>
                      <span className="goals-stat-value">{totalGoals}</span>
                    </div>
                  </div>

                  <div className="goals-stat-card completed">
                    <div className="goals-stat-icon"><i className="fas fa-check-circle"></i></div>
                    <div className="goals-stat-content">
                      <span className="goals-stat-label">Atingidas</span>
                      <span className="goals-stat-value">{completedGoals}</span>
                      <span className="goals-stat-subtext">
                        {totalGoals > 0 ? ((completedGoals / totalGoals) * 100).toFixed(1) : 0}% do total
                      </span>
                    </div>
                  </div>

                  <div className="goals-stat-card active">
                    <div className="goals-stat-icon"><i className="fas fa-play-circle"></i></div>
                    <div className="goals-stat-content">
                      <span className="goals-stat-label">Não Alcançadas</span>
                      <span className="goals-stat-value">{activeGoals}</span>
                      <span className="goals-stat-subtext">
                        {totalGoals > 0 ? ((activeGoals / totalGoals) * 100).toFixed(1) : 0}% do total
                      </span>
                    </div>
                  </div>

                  <div className="goals-stat-card progress">
                    <div className="goals-stat-icon"><i className="fas fa-chart-line"></i></div>
                    <div className="goals-stat-content">
                      <span className="goals-stat-label">Progresso</span>
                      <span className="goals-stat-value">{avgProgress.toFixed(1)}%</span>
                      <span className="goals-stat-subtext">Média de metas positivas</span>
                    </div>
                  </div>
                </div>

                {/* Units */}
                <div className="operation-content">
                  <div className="units-grid">
                    {Object.keys(operationGoals).map(unitName => (
                      <article key={unitName} className="unit-section">
                        <div className="period-header period-header-bg-goals">
                          <h4 className="period-title">
                            <i className="fas fa-industry"></i>
                            {unitName}
                          </h4>
                        </div>
                        <div className="goals-cards-grid">
                          {operationGoals[unitName].map(goal => (
                            <GoalCard key={goal.id} goal={goal} onUpdate={handleGoalUpdate} />
                          ))}
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default GoalList;
